import type { DefLookup } from "../catalog";
import { deviceDesignModes } from "../cable-demands";
import { isAcTerminalId } from "../ac";
import { dcPortTerminalGroups, isBatteryPoweredInverter, isInverterCharger, isPvTerminalId } from "../component-ports";
import { outputPortCurrent } from "../output-current-limits";
import { buildDcCircuitTopology, dcInputPorts, type DcCircuitTopology, type DcPort, type DcSourcePair } from "../dc-circuit-topology";
import { resolveDesignCurrentGroup } from "../dc-design-currents";
import { computeIsolatorModelBoundary } from "../dc-isolators";
import type { ComponentDefinition, Issue, System } from "../types";

type DemandCheck = {
  batteryIds: string[];
  limitA: number;
  byDevice: Map<string, number>;
};

const positiveFinite = (value: number | undefined): value is number =>
  value != null && Number.isFinite(value) && value > 0;

/** Source voltage evidence only: the topology's nominal 12 V fallback is not
 * a declaration. Unequal parallel voltages do not establish one operating V. */
export function declaredSourceVoltage(source: DcSourcePair, definitions: ReadonlyMap<string, ComponentDefinition>): number | undefined {
  if (source.kind === "converter-output") {
    const voltage = definitions.get(source.nodeId!)?.outputVoltage;
    return positiveFinite(voltage) ? voltage : undefined;
  }
  let voltage = 0;
  for (const level of source.levels) {
    const values = level.map(id => definitions.get(id)?.voltage);
    if (!values.length || !values.every(positiveFinite) || values.some(value => Math.abs(value - values[0]) > 1e-6)) return undefined;
    voltage += values[0];
  }
  return positiveFinite(voltage) ? voltage : undefined;
}

function levelDischargeLimit(level: string[], definitions: ReadonlyMap<string, ComponentDefinition>): number | undefined {
  const ratings = level.map(id => definitions.get(id)?.maxDischargeA);
  return ratings.length && ratings.every(positiveFinite)
    ? ratings.reduce((sum, rating) => sum + rating, 0) : undefined;
}

/** Parallel ratings add within each level. Every series level carries the same current. */
export function batteryRangeDischargeLimit(source: DcSourcePair, definitions: ReadonlyMap<string, ComponentDefinition>): number | undefined {
  const limits = source.levels.map(level => levelDischargeLimit(level, definitions));
  return limits.length && limits.every(positiveFinite) ? Math.min(...limits) : undefined;
}

/** A connected converter's rated-output design case, with a loss-free lower
 * bound when efficiency is undeclared. Keep allocation among multiple ports
 * unverified; neither missing output wiring nor default voltages prove input
 * demand. This deliberately does not change the cable-sizing planning model. */
function knownConverterInput(
  nodeId: string, def: ComponentDefinition, topology: DcCircuitTopology,
  definitions: ReadonlyMap<string, ComponentDefinition>, wired: ReadonlySet<string> | undefined,
  consumerPorts: readonly DcPort[],
): number | undefined {
  const ports = (side: "input" | "output"): DcPort[] => dcPortTerminalGroups(def, side)
    .filter(terms => !terms.some(t => isPvTerminalId(t.id)))
    .map((terms, index) => ({
      id: `${nodeId}:${side}:${index}`, nodeId,
      positive: terms.filter(t => t.polarity === "positive").map(t => `${nodeId}:${t.id}`),
      negative: terms.filter(t => t.polarity !== "positive").map(t => `${nodeId}:${t.id}`),
    }))
    .filter(port => port.positive.length === 1 && port.negative.length > 0 &&
      [...port.positive, ...port.negative].every(vertex => wired?.has(vertex.slice(nodeId.length + 1))));
  const inputs = ports("input"), outputs = ports("output");
  if (inputs.length !== 1 || outputs.length !== 1 || !positiveFinite(def.ampOutput)) return undefined;
  const input = topology.resolvePort(inputs[0]), output = topology.resolvePort(outputs[0]);
  if (input.unsupported.length || output.unsupported.length) return undefined;
  const batteryInputs = input.routes.filter(route => route.source.kind === "battery");
  const inputVoltages = (batteryInputs.length ? batteryInputs : input.routes)
    .map(route => declaredSourceVoltage(route.source, definitions));
  if (!inputVoltages.length || !inputVoltages.every(positiveFinite)) return undefined;
  const batteryOutputs = output.routes.filter(route => route.source.kind === "battery");
  let outputVoltage: number;
  if (batteryOutputs.length) {
    const voltages = batteryOutputs.map(route => declaredSourceVoltage(route.source, definitions));
    if (!voltages.every(positiveFinite)) return undefined;
    outputVoltage = Math.min(...voltages);
  } else {
    // A batteryless load must close a loop on this exact declared output pair.
    // A voltage declaration alone does not make an unwired output complete.
    if (!positiveFinite(def.outputVoltage)) return undefined;
    const sameTerminals = (a: string[], b: string[]) => a.length === b.length && a.every(v => b.includes(v));
    const completeLoad = consumerPorts.some(port => {
      if (port.nodeId === nodeId || definitions.get(port.nodeId)?.category === "engine") return false;
      const resolution = topology.resolvePort(port);
      return !resolution.unsupported.length && resolution.routes.some(route =>
        route.source.kind === "converter-output" && route.source.nodeId === nodeId &&
        sameTerminals(route.source.positive, outputs[0].positive) && sameTerminals(route.source.negative, outputs[0].negative));
    });
    if (!completeLoad) return undefined;
    outputVoltage = def.outputVoltage;
  }
  const outputAmps = outputPortCurrent(def, outputs[0].positive[0].slice(nodeId.length + 1), def.ampOutput);
  const efficiency = positiveFinite(def.conversionEfficiency) && def.conversionEfficiency <= 1 ? def.conversionEfficiency : 1;
  const inputAmps = outputAmps * outputVoltage / (Math.max(...inputVoltages) * efficiency);
  return positiveFinite(def.maxInputA) ? Math.min(inputAmps, def.maxInputA) : inputAmps;
}

/** Known simultaneous sustained demand, with no optional charging credit.
 * Complete paired routes identify the battery paths; terminal ratings, surge,
 * provisional fallback currents and chemistry estimates cannot supply a value.
 * Parallel sums are aggregate declared bounds, never a sharing guarantee. */
export function detectCombinedBatteryDischarge(system: System, lookup: DefLookup): Issue[] {
  const topology = buildDcCircuitTopology(system, lookup);
  if (topology.unsupported.length) return [];
  const isolatorBoundary = computeIsolatorModelBoundary(system, lookup);
  const consumerPorts = dcInputPorts(system, lookup);
  const definitions = new Map(system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    return def ? [[node.id, def] as const] : [];
  }));
  const wired = new Map<string, Set<string>>();
  for (const cable of system.cables) {
    for (const [id, terminal] of [[cable.fromNodeId, cable.fromTerminalId], [cable.toNodeId, cable.toTerminalId]]) {
      const terminals = wired.get(id) ?? new Set<string>();
      terminals.add(terminal); wired.set(id, terminals);
    }
  }
  const levelKey = (level: string[]) => [...level].sort().join("|");
  const pairKey = (source: DcSourcePair) =>
    `${topology.railId(source.positive[0])}|${topology.railId(source.negative[0])}`;
  const checks = new Map<string, DemandCheck>();
  const credit = (key: string, batteryIds: string[], limitA: number, nodeId: string, amps: number) => {
    const check = checks.get(key) ?? { batteryIds, limitA, byDevice: new Map<string, number>() };
    // Modes belonging to the same device are alternatives, not simultaneous loads.
    check.byDevice.set(nodeId, Math.max(check.byDevice.get(nodeId) ?? 0, amps));
    checks.set(key, check);
  };
  for (const node of system.nodes) {
    const def = definitions.get(node.id);
    if (!def || !["load", "monitor", "charger"].includes(def.category)) continue;
    let draw = def.ampDraw ?? 0;
    if (def.category === "load" && def.loadCurrentModel === "constant-power" &&
        def.watts != null && Number.isFinite(def.watts) && def.watts >= 0) {
      // Constant-power amps require the supplying pair's declared voltage.
      // A load's reference voltage/amps and the cable model's 12 V default
      // cannot establish its operating current on an unknown supply.
      const inputs = consumerPorts.filter(port => port.nodeId === node.id);
      if (inputs.length !== 1) continue;
      const resolution = topology.resolvePort(inputs[0]);
      const batteryRoutes = resolution.routes.filter(route => route.source.kind === "battery");
      const voltages = (batteryRoutes.length ? batteryRoutes : resolution.routes)
        .map(route => declaredSourceVoltage(route.source, definitions));
      if (resolution.unsupported.length || !voltages.length || !voltages.every(positiveFinite)) continue;
      draw = def.watts / Math.max(...voltages);
    }
    const converter = def.category === "charger" && !isInverterCharger(def) && def.terminals.some(t =>
      t.direction === "input" && t.polarity === "positive" && !isAcTerminalId(t.id) && !isPvTerminalId(t.id));
    const explicitInput = def.ampDraw != null && Number.isFinite(def.ampDraw) && def.ampDraw >= 0;
    const converterInput = converter
      ? explicitInput ? def.ampDraw : knownConverterInput(node.id, def, topology, definitions, wired.get(node.id), consumerPorts)
      : undefined;
    if (converter && converterInput == null) continue;
    const whole = {
      draw, surge: def.ampSurge ?? 0, output: def.ampOutput ?? 0, converterInput,
    };
    const request = deviceDesignModes(node.id, def, whole, wired.get(node.id));
    if (request.unresolved.length) continue;
    for (const mode of request.modes) {
      const ports = mode.ports.filter(({ current }) => Number.isFinite(current.sustainedA) && current.sustainedA > 0);
      // The canonical model rejects shared aggregate ratings on multiple inputs.
      // Keep that boundary here rather than assigning a stage rating per port.
      if (ports.length !== 1) continue;
      const drawMode = { ...mode, ports };
      if (resolveDesignCurrentGroup(topology, node.id, [drawMode]).unresolved.length) continue;
      const { port, current } = ports[0];
      const resolution = topology.resolvePort(port);
      // One-way transfers may hide another supplying battery from the paired
      // graph. Preserve the canonical uncertainty boundary instead of treating
      // the visible batteries as a complete discharge-capacity bound.
      if (resolution.routes.some(route => [...route.positive, ...route.negative, ...route.series]
        .some(step => isolatorBoundary.cableIds.has(step.cableId)))) continue;
      const sources = [...new Map(resolution.routes.filter(route => route.source.kind === "battery")
        .map(route => [route.sourceId, route.source] as const)).values()];
      if (!sources.length) continue; // A converter output is not a bridge to its input battery.
      const amps = current.sustainedA;
      // Every mandatory parallel level carries this whole demand, including
      // loads on a midpoint and loads across the entire series string.
      for (const level of sources[0].levels) {
        const key = levelKey(level), limit = levelDischargeLimit(level, definitions);
        if (limit == null || !sources.every(source => source.levels.some(other => levelKey(other) === key))) continue;
        credit(`level:${key}`, level, limit, node.id, amps);
      }
      // Parallel strings may have no mandatory level. Sum each path's weakest
      // declared level as an upper bound. Shared cells can only lower that
      // bound; no capacity-based or equal-current allocation is assumed.
      const limits = sources.map(source => batteryRangeDischargeLimit(source, definitions));
      if (limits.some(limit => limit == null) || !sources.every(source => pairKey(source) === pairKey(sources[0]))) continue;
      const batteries = [...new Set(sources.flatMap(source => source.batteryNodeIds))].sort();
      credit(`bank:${pairKey(sources[0])}`, batteries, limits.reduce<number>((sum, a) => sum + a!, 0), node.id, amps);
    }
  }
  const issues = new Map<string, Issue>();
  for (const check of checks.values()) {
    const drawA = [...check.byDevice.values()].reduce((sum, amps) => sum + amps, 0);
    if (drawA <= check.limitA + 1e-6) continue;
    const loadIds = [...check.byDevice.keys()].sort();
    const groupKey = `${loadIds.join("|")}:${drawA}:${check.limitA}`;
    const existing = issues.get(groupKey);
    if (existing) {
      existing.nodeIds = [...new Set([...(existing.nodeIds ?? []), ...check.batteryIds])].sort();
      continue;
    }
    const precision = Math.round(drawA) > Math.round(check.limitA) ? 0
      : Math.min(6, Math.max(1, Math.ceil(-Math.log10(drawA - check.limitA)) + 1));
    // A mandatory rated level can prove an inverter overload even when another
    // battery's rating is unknown. Use this check's exact per-device demand,
    // not a chemistry estimate or the aggregate demand of unrelated loads.
    const inverterOverload = [...check.byDevice].some(([nodeId, amps]) => {
      const def = definitions.get(nodeId)!;
      return isBatteryPoweredInverter(def) && amps > check.limitA + 1e-6;
    });
    issues.set(groupKey, {
      code: "combined-battery-discharge",
      severity: inverterOverload ? "critical" : "warning",
      nodeIds: [...check.batteryIds, ...loadIds].sort(), cableIds: [],
      title: { key: "diag.combined-battery-discharge.title" },
      message: { key: "diag.combined-battery-discharge.message", vars: {
        draw: +drawA.toFixed(precision), limit: +check.limitA.toFixed(precision), count: loadIds.length,
      } },
      fix: { key: "diag.combined-battery-discharge.fix" },
    });
  }
  return [...issues.values()];
}
